import { Outside, NumberInput, InputGroup, InputAdornment } from '@cian/ui-kit';
import { mergeStyles } from '@cian/utils';
import React, { useCallback, useEffect, useRef, useState, FC } from 'react';

import { IJsonQueryRangeValue } from '../../utils/jsonQuery';
import { getPriceLabel, getPriceTitle } from '../../utils/price';
import stylesCommon from '../FiltersPanel/FiltersPanel.css';
import styles from './FilterPrice.css';
import { isValidInput } from './helpers';

export interface IFilterPriceProps {
  value: IJsonQueryRangeValue;
  setMin(value: number | undefined): void;
  setMax(value: number | undefined): void;
}

export interface IFilterPriceState {
  active: boolean;
  min: number | undefined;
  max: number | undefined;
  isErroneous: boolean;
}

export const FilterPrice: FC<IFilterPriceProps> = ({ value, setMax, setMin }) => {
  const [{ active, min, max, isErroneous }, setState] = useState({
    active: false,
    min: value.gte,
    max: value.lte,
    isErroneous: false,
  });
  const validationTimeout = useRef<number>();

  const validate = useCallback(() => {
    setState(prevState => ({ ...prevState, isErroneous: isValidInput(min, max) }));
  }, [max, min]);

  const handleButtonClick = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      active: !active,
    }));
  }, [active]);

  const handleOutsideClick = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      active: false,
    }));
  }, []);

  const handleMinValueChange = useCallback((value: number | undefined) => {
    setState(prevState => ({
      ...prevState,
      min: value,
      isErroneous: prevState.isErroneous ? isValidInput(value, prevState.max) : false,
    }));
  }, []);

  const handleMaxValueChange = useCallback((value: number | undefined) => {
    setState(prevState => ({
      ...prevState,
      max: value,
      isErroneous: prevState.isErroneous ? isValidInput(prevState.min, value) : false,
    }));
  }, []);

  const handleMinBlur = useCallback(() => {
    if (value.gte !== min) {
      setMin(min);
    }
  }, [min, setMin, value.gte]);

  const handleMaxBlur = useCallback(() => {
    if (value.lte !== max) {
      setMax(max);
    }
  }, [max, setMax, value.lte]);

  useEffect(() => {
    if (!isErroneous) {
      if (validationTimeout.current) {
        window.clearTimeout(validationTimeout.current);
      }

      validationTimeout.current = window.setTimeout(validate, 500);
    }
  }, [isErroneous, validate]);

  useEffect(() => {
    return () => {
      if (validationTimeout.current) {
        window.clearTimeout(validationTimeout.current);
      }
    };
  }, []);

  const priceTitle = getPriceTitle(min, max) || undefined;

  const priceLabel = getPriceLabel(min, max);

  return (
    <Outside onOutside={handleOutsideClick}>
      <div
        {...mergeStyles(stylesCommon['container'], styles['container'])}
        data-mark="FilterPrice"
        data-name="FilterPrice"
      >
        <button
          {...mergeStyles(
            stylesCommon['button'],
            active && stylesCommon['button--active'],
            !priceLabel && stylesCommon['button--empty'],
          )}
          title={priceTitle}
          onClick={handleButtonClick}
        >
          {priceLabel || 'Цена'}
        </button>
        {active && (
          <div {...mergeStyles(stylesCommon['dropdown'], styles['dropdown'])}>
            <InputGroup>
              <NumberInput
                placeholder="от"
                invalid={isErroneous}
                rightAdornment={<InputAdornment>{'\u20bd'}</InputAdornment>}
                min={0}
                value={min}
                onChange={handleMinValueChange}
                onBlur={handleMinBlur}
              />
              <NumberInput
                placeholder="до"
                invalid={isErroneous}
                rightAdornment={<InputAdornment>{'\u20bd'}</InputAdornment>}
                min={0}
                value={max}
                onChange={handleMaxValueChange}
                onBlur={handleMaxBlur}
              />
            </InputGroup>
          </div>
        )}
      </div>
    </Outside>
  );
};
